/**
 * Story SF-CHAT-8.2 — decisões do cartão de prévia de ação sobre contas a pagar.
 *
 * Puras, para serem testadas em Node: o componente só desenha o que estas
 * funções decidem. O cartão nunca vê o `action_id` do app — só o `preview_id`
 * do chat, que sozinho não autoriza nada.
 *
 * Story SF-CHAT-8.3: `agendar_pagamento` muda só a previsão de pagamento. O
 * item traz a previsão atual e a nova (em vez da situação de aprovação), e a
 * nova precisa ser a data do cartão — senão o chunk é recusado.
 */

export type PayablesAction = "solicitar_aprovacao" | "aprovar" | "rejeitar" | "agendar_pagamento";
export type ButtonKind = "confirm" | "cancel" | "refresh";

export interface ActionItem {
  id: number;
  descricao: string;
  fornecedor: string;
  valorBruto: string;
  dataVencimento: string | null;
  statusValidacaoAtual: string;
  statusValidacaoNovo: string;
  /** Só `agendar_pagamento` (ISO); `null` = sem previsão hoje. */
  previsaoAtual?: string | null;
  previsaoNova?: string;
}

/** Linha do resultado: situação de aprovação ou, no agendar, a previsão gravada. */
export interface ActionResultItem {
  id: number;
  status_validacao?: string;
  data_previsao_pagamento?: string;
}

export interface ActionCard {
  previewId: string;
  action: PayablesAction;
  itens: ActionItem[];
  quantidade: number;
  totalValorBruto: string;
  expiresAt: string;
  /** Só `agendar_pagamento`: a data que a confirmação grava (ISO). */
  dataPrevisao?: string;
}

export interface ActionButtonResponse {
  result_status: string;
  response?: string;
  action_result?: { action?: string; replay?: boolean; itens?: ActionResultItem[] };
  action_preview?: unknown;
}

export type CardState = "open" | "executed" | "replay" | "cancelled" | "expired" | "stale" | "unavailable" | "error";

const ACTIONS: PayablesAction[] = ["solicitar_aprovacao", "aprovar", "rejeitar", "agendar_pagamento"];
const ISO_DATE = /^\d{4}-\d{2}-\d{2}$/;

function isText(v: unknown): v is string {
  return typeof v === "string";
}

/** O chunk `action_preview` do backend, ou `null` se não bate com o contrato. */
export function parseActionPreview(raw: unknown): ActionCard | null {
  if (!raw || typeof raw !== "object") return null;
  const c = raw as Record<string, unknown>;
  if (!isText(c.preview_id) || !c.preview_id) return null;
  if (!ACTIONS.includes(c.action as PayablesAction)) return null;
  if (!Array.isArray(c.itens) || c.itens.length === 0) return null;
  if (typeof c.quantidade !== "number" || c.quantidade !== c.itens.length) return null;
  if (!isText(c.total_valor_bruto) || !isText(c.expires_at) || Number.isNaN(Date.parse(c.expires_at))) return null;
  const agendar = c.action === "agendar_pagamento";
  const dataPrevisao = c.data_previsao_pagamento;
  if (agendar && !(isText(dataPrevisao) && ISO_DATE.test(dataPrevisao))) return null;
  const itens: ActionItem[] = [];
  for (const bruto of c.itens) {
    const i = bruto as Record<string, unknown>;
    if (!i || typeof i.id !== "number" || !isText(i.valor_bruto)) return null;
    if (agendar) {
      if (i.data_previsao_pagamento_nova !== dataPrevisao) return null;
      itens.push({
        id: i.id,
        descricao: isText(i.descricao) ? i.descricao : "",
        fornecedor: isText(i.fornecedor) ? i.fornecedor : "",
        valorBruto: i.valor_bruto,
        dataVencimento: isText(i.data_vencimento) ? i.data_vencimento : null,
        statusValidacaoAtual: "",
        statusValidacaoNovo: "",
        previsaoAtual: isText(i.data_previsao_pagamento_atual) ? i.data_previsao_pagamento_atual : null,
        previsaoNova: dataPrevisao as string,
      });
      continue;
    }
    if (!isText(i.status_validacao_novo)) return null;
    itens.push({
      id: i.id,
      descricao: isText(i.descricao) ? i.descricao : "",
      fornecedor: isText(i.fornecedor) ? i.fornecedor : "",
      valorBruto: i.valor_bruto,
      dataVencimento: isText(i.data_vencimento) ? i.data_vencimento : null,
      statusValidacaoAtual: isText(i.status_validacao_atual) ? i.status_validacao_atual : "",
      statusValidacaoNovo: i.status_validacao_novo,
    });
  }
  return {
    previewId: c.preview_id,
    action: c.action as PayablesAction,
    itens,
    quantidade: c.quantidade,
    totalValorBruto: c.total_valor_bruto,
    expiresAt: c.expires_at,
    ...(agendar ? { dataPrevisao: dataPrevisao as string } : {}),
  };
}

/** "1104.20" → "R$ 1.104,20", direto da string decimal (sem float). */
export function formatBRL(value: string): string {
  const m = /^(-?)(\d+)(?:\.(\d{1,2}))?$/.exec(String(value).trim());
  if (!m) return "valor não informado";
  const [, sinal, inteiro, centavos = ""] = m;
  const milhar = inteiro.replace(/^0+(?=\d)/, "").replace(/\B(?=(\d{3})+(?!\d))/g, ".");
  return `${sinal}R$ ${milhar},${centavos.padEnd(2, "0")}`;
}

export function formatDate(iso: string | null): string {
  if (!iso) return "sem vencimento";
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso);
  return m ? `${m[3]}/${m[2]}/${m[1]}` : iso;
}

/** Previsão de pagamento: "05/11/2026" ou "sem previsão". */
export function formatPrevisao(iso: string | null | undefined): string {
  return iso ? formatDate(iso) : "sem previsão";
}

export function actionLabel(action: PayablesAction): string {
  return {
    solicitar_aprovacao: "Solicitar aprovação",
    aprovar: "Aprovar",
    rejeitar: "Rejeitar",
    agendar_pagamento: "Agendar pagamento",
  }[action];
}

export function confirmLabel(action: PayablesAction, n: number): string {
  const nome = {
    solicitar_aprovacao: "pedido de aprovação",
    aprovar: "aprovação",
    rejeitar: "rejeição",
    agendar_pagamento: "agendamento",
  }[action];
  return `Confirmar ${nome} de ${n} ${n === 1 ? "conta" : "contas"}`;
}

export function statusLabel(status: string): string {
  const nomes: Record<string, string> = {
    pendente: "Aguardando aprovação",
    validado: "Aprovada",
    nao_se_aplica: "Rejeitada",
    "": "Sem situação",
  };
  return nomes[status] ?? status;
}

export function secondsLeft(expiresAt: string, nowMs: number): number {
  return Math.max(0, Math.ceil((Date.parse(expiresAt) - nowMs) / 1000));
}

export function formatCountdown(seconds: number): string {
  const s = Math.max(0, Math.floor(seconds));
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, "0")}`;
}

/** Texto para leitor de tela só ao cruzar 60 s, 30 s e o fim — nunca a cada segundo. */
export function countdownAnnouncement(previous: number, current: number): string | null {
  if (previous > 60 && current <= 60 && current > 30) return "Falta 1 minuto para a prévia expirar.";
  if (previous > 30 && current <= 30 && current > 0) return "Faltam 30 segundos para a prévia expirar.";
  // O fim é dito pelo próprio cartão (`role="status"`); anunciar aqui duplicaria.
  return null;
}

/**
 * Estado do cartão para o status devolvido pelo botão. `keepsError`: a prévia
 * continua valendo e o botão Confirmar continua disponível, com o aviso à vista.
 */
export function cardStateFor(status: string): { state: CardState; keepsError: boolean } {
  switch (status) {
    case "action_preview":
      return { state: "open", keepsError: false };
    case "action_executed":
      return { state: "executed", keepsError: false };
    case "action_replay":
      return { state: "replay", keepsError: false };
    case "action_cancelled":
      return { state: "cancelled", keepsError: false };
    case "action_expired":
      return { state: "expired", keepsError: false };
    case "action_stale_preview":
      return { state: "stale", keepsError: false };
    case "action_unavailable":
      return { state: "unavailable", keepsError: false };
    case "action_not_refreshable":
    case "action_transport_unavailable":
    case "context_expired":
    case "context_rejected":
    case "context_unavailable":
    case "grant_required":
    case "grant_rejected":
      return { state: "open", keepsError: true };
    default:
      return { state: "error", keepsError: false };
  }
}

export interface CardUpdate {
  /** Cartão novo (refazer a prévia); ausente mantém o atual. */
  card?: ActionCard;
  state: CardState;
  notice: string | null;
  result: ActionResultItem[] | null;
}

/** O que o cartão faz com a resposta de um botão. Pura, para ser testada. */
export function reduceCardResponse(kind: ButtonKind, r: ActionButtonResponse): CardUpdate {
  const novo = kind === "refresh" ? parseActionPreview(r.action_preview) : null;
  if (novo) return { card: novo, state: "open", notice: null, result: null };
  const { state, keepsError } = cardStateFor(r.result_status);
  const result = state === "executed" || state === "replay" ? r.action_result?.itens ?? [] : null;
  const notice = keepsError || state === "error" || state === "stale"
    ? r.response || "Não foi possível concluir. Nada foi alterado."
    : null;
  return { state: keepsError ? "open" : state, notice, result };
}

type FetchLike = (input: string, init?: RequestInit) => Promise<Response>;

/** Um clique: grant novo do app e a rota do botão. Nada aqui é guardado. */
export async function postActionButton(options: {
  kind: ButtonKind;
  previewId: string;
  sessionId: string;
  correlationId: string;
  requestTurnGrant: () => Promise<string>;
  fetchImpl?: FetchLike;
}): Promise<ActionButtonResponse> {
  const grant = await options.requestTurnGrant();
  const doFetch: FetchLike = options.fetchImpl ?? ((i, init) => fetch(i, init));
  let resposta: Response;
  try {
    resposta = await doFetch(`/api/chat/actions/${options.kind}`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        session_id: options.sessionId,
        correlation_id: options.correlationId,
        preview_id: options.previewId,
        grant,
      }),
    });
  } catch {
    throw new Error("O backend do chat não respondeu. Nada foi alterado por este clique; tente de novo.");
  }
  const corpo = (await resposta.json().catch(() => null)) as ActionButtonResponse | null;
  if (!resposta.ok || !corpo || typeof corpo.result_status !== "string") {
    throw new Error("O chat está indisponível agora. Confira a situação das contas no aplicativo antes de tentar de novo.");
  }
  return corpo;
}
